/**
 * Central registry of all guide articles.
 * Markdown content is imported as raw strings at build time via Vite's ?raw suffix.
 */

/* ── raw markdown imports ─────────────────────────────────────────────── */
import guide01 from "../../guide/01-getting-started.md?raw";
import guide02 from "../../guide/02-recording.md?raw";
import guide03 from "../../guide/03-editing-timeline.md?raw";
import guide04 from "../../guide/04-animation-keyframes.md?raw";
import guide05 from "../../guide/05-auto-zoom.md?raw";
import guide06 from "../../guide/06-cursor-mouse.md?raw";
import guide07 from "../../guide/07-look-and-feel.md?raw";
import guide08 from "../../guide/08-audio.md?raw";
import guide09 from "../../guide/09-privacy-background-removal.md?raw";
import guide10 from "../../guide/10-exporting.md?raw";

/** Strip YAML/markdown frontmatter (--- ... ---) from markdown string */
export function stripFrontmatter(md) {
  return md.replace(/^---[\s\S]*?---\s*/, "");
}

/**
 * Remove standalone horizontal rules (---) from guide markdown.
 * The h2 heading style already includes a top border divider,
 * so explicit --- create a visible double-divider.
 */
export function stripHorizontalRules(md) {
  return md.replace(/^\s*---\s*$/gm, "");
}

/** Icon name constants for each guide section */
const icons = {
  "getting-started": "rocket",
  recording: "record",
  "editing-timeline": "timeline",
  "animation-keyframes": "animation",
  "auto-zoom": "zoom",
  "cursor-mouse": "cursor",
  "look-and-feel": "palette",
  audio: "audio",
  "privacy-background-removal": "privacy",
  exporting: "export",
};

/** All guide articles in display order */
export const guideArticles = [
  {
    slug: "getting-started",
    title: "Getting Started",
    description:
      "Installation, system requirements, permissions setup, creating your first project, and UI overview.",
    icon: icons["getting-started"],
    raw: guide01,
  },
  {
    slug: "recording",
    title: "Recording",
    description:
      "Screen recording, window recording, area recording, camera-only recording, audio sources, and camera PiP.",
    icon: icons["recording"],
    raw: guide02,
  },
  {
    slug: "editing-timeline",
    title: "Editing & Timeline",
    description:
      "Editor layout, timeline navigation, layer management, playback controls, crop tool, split tool, and media import.",
    icon: icons["editing-timeline"],
    raw: guide03,
  },
  {
    slug: "animation-keyframes",
    title: "Animation & Keyframes",
    description:
      "Keyframe system, animatable properties, easing types, cubic bezier curves, spring easing, and the curves panel.",
    icon: icons["animation-keyframes"],
    raw: guide04,
  },
  {
    slug: "auto-zoom",
    title: "Auto Zoom",
    description:
      "Auto zoom modes, session-based zoom, saliency analysis, motion blur, zoom settings, and easing options.",
    icon: icons["auto-zoom"],
    raw: guide05,
  },
  {
    slug: "cursor-mouse",
    title: "Cursor & Mouse",
    description:
      "Cursor styles, size, path smoothing, click sounds, click ripple, and cursor animation settings.",
    icon: icons["cursor-mouse"],
    raw: guide06,
  },
  {
    slug: "look-and-feel",
    title: "Look & Feel",
    description:
      "Backgrounds, gradients, shadows, corner radius, opacity, strokes, text styling, and canvas aspect ratio.",
    icon: icons["look-and-feel"],
    raw: guide07,
  },
  {
    slug: "audio",
    title: "Audio",
    description:
      "AI noise reduction, audio sources, voiceover, gain & normalization, EQ, and compression settings.",
    icon: icons["audio"],
    raw: guide08,
  },
  {
    slug: "privacy-background-removal",
    title: "Background Removal",
    description:
      "Remove backgrounds from videos and images with automatic detection and background blur.",
    icon: icons["privacy-background-removal"],
    raw: guide09,
  },
  {
    slug: "exporting",
    title: "Exporting",
    description:
      "MP4 and GIF export, resolution settings, quality presets, audio mixdown, and batch export.",
    icon: icons["exporting"],
    raw: guide10,
  },
];

/** Lookup a guide article by slug */
export function getGuideBySlug(slug) {
  return guideArticles.find((a) => a.slug === slug) ?? null;
}

/** Get previous and next guide articles for navigation */
export function getGuideNav(slug) {
  const idx = guideArticles.findIndex((a) => a.slug === slug);
  return {
    prev: idx > 0 ? guideArticles[idx - 1] : null,
    next: idx < guideArticles.length - 1 ? guideArticles[idx + 1] : null,
  };
}
